import { AppContext } from "../core/context";
import { materialLibrary, meshLibrary, productLibrary } from "../data";
import { hideAllHighlights } from "../scene/highlight";
import { cleanupAssets, pauseRender, resumeRender } from "../scene/renderer";
import { Entity, Module, Product } from "../types/type_def";
import { DataHelper } from "../utils/data";
import { hideEntityActions } from "./actions";
import { connectNeighbourEntities, createEntityWeights, instantiateEntity, instantiateModule } from "./entities";
import { constructLegs, createLegAttachments } from "./legs";
import { onProgress } from "./progress";

export function getDefaultDepth(ctx: AppContext) {
    const widths = [];
    const depths = [];

    productLibrary.productsCategories.forEach((category) => {
        category.products.forEach((product) => {
        widths.push(product.width);
        depths.push(product.depth);
        });
    });

    var widthData = DataHelper.findDuplicate(widths);
    var depthData = DataHelper.findDuplicate(depths);

    return widthData.repeated > depthData.repeated ? widthData.value : depthData.value;
}

export function getDefaultHeight(ctx: AppContext) {
    const heights = [];

    productLibrary.productsCategories.forEach((category) => {
        category.products.forEach((product) => {
            heights.push(product.height);            
        });
    });

    var heightData = DataHelper.findDuplicate(heights);
    return heightData.value
}

export function getSeriesDepth(ctx: AppContext, entity?: Entity, connectorId?: string): number {
    let seriesDepth = getDefaultDepth(ctx);

    if (entity) {
        const entityType = entity.category.toLowerCase();
        switch (ctx.currentConfiguration.series.toLowerCase()) {
            case "bure plain w. down top":
            case "bure plain w. fiberfill":
            case "bure wave":
            {
                switch (entityType) {
                    case "back":
                        {
                        if (["CT38", "CT39"].includes(connectorId)) {
                            const productCategory = productLibrary.productsCategories.filter((x) => x.type.toLowerCase() === "back");
                            const product = productCategory[0].products.find((x) => x.type.toLowerCase() === "back");
                            seriesDepth = product.depth;
                        }
                        }
                        break;
                    case "seatframe": {
                        if (["CT03", "CT04"].includes(connectorId)) {
                            const productCategory = productLibrary.productsCategories.filter((x) => x.type.toLowerCase() === "back");
                            const product = productCategory[0].products.find((x) => x.type.toLowerCase() === "back");
                            seriesDepth = product.depth;
                        }
                    }
                        break;
                    case "arm": {
                        if (["CT44", "CT45"].includes(connectorId)) {
                            const productCategory = productLibrary.productsCategories.filter((x) => x.type.toLowerCase() === "back");
                            const product = productCategory[0].products.find((x) => x.type.toLowerCase() === "back");
                            seriesDepth = product.depth;
                        }
                    }
                        break;
                }
            }
            break;
        }              
    }

    return seriesDepth;    
}

export function getProductVariant(ctx: AppContext, productNo: string, entity: Entity) {

    let product = {} as Product;
    let productVariant = {} as Product; 

    let connectionType = ctx.moduleHelper.getEntityConnectionType(ctx.currentModule, entity);

    product = productLibrary.getProductByProductNo(productNo);

    if (!product)
        return;
    
    Object.assign(productVariant, product);

    let productConnectionTypeVariant = productLibrary.getProductVariantByConnectionType(product, connectionType);

    if (!productConnectionTypeVariant) {        
        productConnectionTypeVariant = productLibrary.getProductVariantByProductNo(product);
    }

    if (productConnectionTypeVariant) {
        productVariant.productNo = productConnectionTypeVariant.productNo;
        productVariant.axProductNo = productConnectionTypeVariant.axProductNo;
        productVariant.name = productConnectionTypeVariant.name;
        productVariant.model = productConnectionTypeVariant.model ?? product.model;
        productVariant.isFlipped = productConnectionTypeVariant.isFlipped;
    }

    switch (product.type) {
        case "SOFA": {

            if (entity.connections.length === 1 && product.series === 4220) {

                switch (productConnectionTypeVariant.connectionType) {
                    case "CT06":
                        {
                            productConnectionTypeVariant = productLibrary.getProductVariantByConnectionType(product, "CT05");
                            productVariant.model = productConnectionTypeVariant.model;
                            productVariant.isFlipped = true;
                        }
                        break;
                }
            }
        }
            break;
        case "BACK": {

           /* const entityConnections = entity.connections.map(x => x.id);
            if (entityConnections.includes("CT36") && entityConnections.includes("CT37")) {
                productVariant.model = productVariant.model.replace("one side","two sides");
            }  */             

        }
            break;
        case "FOOTSTOOL": {

           if (productVariant.width !== productVariant.depth) {

                var seriesDepth = getSeriesDepth(ctx);
                var width = productVariant.width;
                var depth = productVariant.depth;

                if (connectionType !== "CT11" && connectionType !== "CT00") {
                    var tmp = depth;
                    depth = width;
                    width = tmp;
                }

                if (width >= seriesDepth && depth >= seriesDepth) {
                    var tmp = width;
                    width = depth;
                    depth = tmp;
                }

                productVariant.depth = depth;
                productVariant.width = width;
            }
        }
    }

    return productVariant;
}

export async function constructModule(ctx: AppContext, module: Module) {

    return new Promise(resolve => {

        //Dispose all UI and models from scene
        cleanupAssets(ctx); 

        if (!module) {
            ctx.ui.hideLoadingCanvas();
            resolve(true);
        }          

        if (module) {

            ctx.ui.showLoadingCanvas();

            instantiateModule(ctx, ctx.currentConfiguration, module, function (instantiatedModule, instantiatedModuleNode) {

                if (module.entities.filter(x => productLibrary.getProductCategory(x.category) === "UPHOLSTRY").length === 0) {
                    ctx.ui.handleModuleUpdating(() => {
                        ctx.ui.hideLoadingCanvas();
                        resolve(true);
                    }); 
                }

                ctx.currentModule = instantiatedModule;
                ctx.currentModuleNode = instantiatedModuleNode;

                const filteredEntities = module.entities.filter(x => productLibrary.getProductCategory(x.category) === "UPHOLSTRY");

                filteredEntities.forEach(function (entity: Entity) {
                    let product = productLibrary.getProductByProductNo(entity.productNo);                           
                    instantiateEntity(ctx, module, product, entity);
                });
               
                hideAllHighlights(ctx);
                hideEntityActions(ctx);                    
              
                constructEntities(ctx, module, async (meshes) => {   
                    
                    ctx.legConnections = [];

                    const filteredEntities = module.entities.filter(x => productLibrary.getProductCategory(x.category) === "UPHOLSTRY")
                
                    for (let entity of filteredEntities) {        
                        var productVariant = getProductVariant(ctx, entity.productNo, entity);
                        if (!productVariant)
                            continue;
                        
                        connectNeighbourEntities(ctx, entity)

                        //Create leg attachments                          
                        createLegAttachments(ctx, module, entity, productVariant, ctx.scene).then(() => {
                            
                            //Create legs
                            constructLegs(ctx, module, entity);
                        });

                    };  
                      
                    //Ares
                   /* var series = seriesLibrary.getSeriesByName(currentConfiguration.series);
                    if (![148881].includes(series.id)) {
                        //moveAllEntities(module);
                    }*/

                   // await updateSummaryMenuTotalPrice();                      

                    pauseRender(ctx);
                    resumeRender(ctx).then(() => {

                        ctx.ui.handleModuleUpdating(() => {
                            resolve(true);
                        });                           
                    
                    }); 

                });  
              
            });                
        }

         
    });
}

export function constructEntities(ctx: AppContext, module: Module, callback: (meshes: any[]) => void) {
  
    let count = 0;
    const meshes = [];

    const filteredEntities = module.entities.filter(x => productLibrary.getProductCategory(x.category) === "UPHOLSTRY");
    for (let entity of filteredEntities) {

        //Create weights to determine best view
        createEntityWeights(ctx, entity);
     
        var productVariant = getProductVariant(ctx, entity.productNo, entity);
        if (!productVariant) {
            meshes.push(null);
            continue;
        }

        entity.variantProductNo = productVariant.productNo;
        const entityNode = ctx.scene.getTransformNodeByUniqueId(entity.uniqueId);            
        entityNode.metadata.productNo = productVariant.productNo;

        var entityBounds = entityNode.getChildMeshes(true, function (m) { return m.id === "entitybounds" })[0];
        entityBounds.setEnabled(true);          

        //Load entity mesh
        meshLibrary.loadMeshAsync(productVariant.productNo, entity, productVariant.type, "furniture/", productVariant.model + ".glb", productVariant.isFlipped, ctx.scene, false, ctx.sg, ctx.sg2, (e) => onProgress(ctx, e), function (clonedMeshEntityId, clonedMesh) {
                     
            let loadedEntity = ctx.moduleHelper.getEntityByID(ctx.currentModule, clonedMeshEntityId);
            var loadedProductVariant = getProductVariant(ctx, loadedEntity.productNo, loadedEntity);

            clonedMesh.scaling.x = loadedProductVariant.isFlipped ? -1 : 1;
            clonedMesh.position.y = ctx.currentConfiguration.data.offsetY;

            //Set material
            let material = materialLibrary.getMaterialByProductNumber(ctx.currentConfiguration.data["material"]);
            if (material) {
                materialLibrary.assignMaterial(loadedProductVariant, [clonedMesh], material, ctx.scene, function () {                     
                    clonedMesh.setEnabled(true);
                });
            }

            meshes.push(clonedMesh);
            if (meshes.length === filteredEntities.length) {
                callback(meshes);                                                                      
            }
        });           
    };
}
